/**
 * Sprint 6B-02 & 6B-04: Lead State Machine & Role-Based Permissions
 * 
 * This utility provides:
 * 1. Valid state transitions for the lead lifecycle
 * 2. Role-based permission checks for state transitions
 * 3. Validation functions for state changes
 */

// =============================================================================
// 6B-02: VALID STATE TRANSITIONS
// Defines which states can transition to which other states
// =============================================================================

/**
 * State transition map - defines valid "from -> to" transitions
 * Key: current status, Value: array of valid next statuses
 */
export const VALID_TRANSITIONS = {
  // Pre-Sales Phase
  NEW: ['ASSIGNED', 'CLOSED_LOST', 'CLOSED_CANCELLED'],
  ASSIGNED: ['CONTACTED', 'NEW', 'CLOSED_LOST', 'CLOSED_CANCELLED'], // Can unassign back to NEW
  CONTACTED: ['OFFER_SENT', 'CLOSED_LOST', 'CLOSED_CANCELLED'],
  
  // Negotiation Phase
  OFFER_SENT: ['DEPOSIT_PAID', 'CONTACTED', 'CLOSED_LOST', 'CLOSED_CANCELLED'], // Can go back to CONTACTED for re-negotiation
  
  // Committed Phase
  DEPOSIT_PAID: ['IN_TREATMENT', 'CLOSED_CANCELLED'], // Cannot go back once deposit paid (refund = cancel)
  
  // Clinical Phase
  IN_TREATMENT: ['POST_OP', 'CLOSED_CANCELLED'], // Patient in clinic - can only move forward or cancel
  POST_OP: ['CLOSED_WON', 'CLOSED_CANCELLED'], // Recovery - success or cancel
  
  // Terminal States - no transitions allowed
  CLOSED_WON: [],
  CLOSED_LOST: [],
  CLOSED_CANCELLED: []
};

/**
 * Validates if a state transition is allowed
 * @param {string} fromStatus - Current status
 * @param {string} toStatus - Target status
 * @returns {object} { valid: boolean, reason?: string }
 */
export const isValidTransition = (fromStatus, toStatus) => {
  // Same status - no transition needed
  if (fromStatus === toStatus) {
    return { valid: false, reason: 'Status is already ' + toStatus };
  }
  
  // Check if fromStatus exists in transition map
  const validNextStates = VALID_TRANSITIONS[fromStatus];
  if (!validNextStates) {
    return { valid: false, reason: `Unknown status: ${fromStatus}` };
  }
  
  // Terminal status check
  if (validNextStates.length === 0) {
    return { valid: false, reason: `Cannot transition from terminal status: ${fromStatus}` };
  }
  
  // Check if toStatus is a valid next state
  if (!validNextStates.includes(toStatus)) {
    return { 
      valid: false, 
      reason: `Cannot transition from ${fromStatus} to ${toStatus}. Valid transitions: ${validNextStates.join(', ')}` 
    };
  }
  
  return { valid: true };
};

/**
 * Gets all valid next statuses from a given status
 * @param {string} currentStatus - Current lead status
 * @returns {string[]} Array of valid next statuses
 */
export const getValidNextStatuses = (currentStatus) => {
  return VALID_TRANSITIONS[currentStatus] || [];
};

// =============================================================================
// 6B-04: ROLE-BASED TRANSITION PERMISSIONS
// Defines which roles can perform which transitions
// =============================================================================

/**
 * Role permission map for state transitions
 * Key: "fromStatus->toStatus", Value: array of roles that can perform this transition
 * 
 * Roles:
 * - clinic_admin: Clinic Administrator (full access)
 * - sales_rep: Sales Representative (pre-sales & negotiation)
 * - doctor: Doctor (clinical phase only)
 * - treatx_admin: TreatX Platform Admin (system override)
 */
export const TRANSITION_PERMISSIONS = {
  // Pre-Sales Transitions (Sales Rep domain)
  'NEW->ASSIGNED': ['provider_admin', 'treatx_admin'],  // Only admin can assign
  'ASSIGNED->NEW': ['provider_admin', 'treatx_admin'],  // Only admin can unassign
  'ASSIGNED->CONTACTED': ['provider_sales_rep', 'provider_admin', 'treatx_admin'],
  'NEW->CLOSED_LOST': ['provider_sales_rep', 'provider_admin', 'treatx_admin'],
  'NEW->CLOSED_CANCELLED': ['provider_admin', 'treatx_admin'],
  'ASSIGNED->CLOSED_LOST': ['provider_sales_rep', 'provider_admin', 'treatx_admin'],
  'ASSIGNED->CLOSED_CANCELLED': ['provider_admin', 'treatx_admin'],
  'CONTACTED->CLOSED_LOST': ['provider_sales_rep', 'provider_admin', 'treatx_admin'],
  'CONTACTED->CLOSED_CANCELLED': ['provider_admin', 'treatx_admin'],
  
  // Negotiation Transitions (Sales Rep domain)
  'CONTACTED->OFFER_SENT': ['provider_sales_rep', 'provider_admin', 'treatx_admin'],
  'OFFER_SENT->CONTACTED': ['provider_sales_rep', 'provider_admin', 'treatx_admin'], // Back to re-negotiate
  'OFFER_SENT->DEPOSIT_PAID': ['provider_sales_rep', 'provider_admin', 'treatx_admin'],
  'OFFER_SENT->CLOSED_LOST': ['provider_sales_rep', 'provider_admin', 'treatx_admin'],
  'OFFER_SENT->CLOSED_CANCELLED': ['provider_admin', 'treatx_admin'],
  
  // Committed Phase - Handoff point (Sales -> Clinical)
  'DEPOSIT_PAID->IN_TREATMENT': ['provider_admin', 'provider_doctor', 'treatx_admin'], // Clinic/Doctor marks arrival
  'DEPOSIT_PAID->CLOSED_CANCELLED': ['provider_admin', 'treatx_admin'], // Refund/Cancel
  
  // Clinical Phase (Doctor/Clinic domain)
  'IN_TREATMENT->POST_OP': ['provider_doctor', 'provider_admin', 'treatx_admin'],
  'IN_TREATMENT->CLOSED_CANCELLED': ['provider_admin', 'treatx_admin'],
  'POST_OP->CLOSED_WON': ['provider_doctor', 'provider_admin', 'treatx_admin'],
  'POST_OP->CLOSED_CANCELLED': ['provider_admin', 'treatx_admin']
};

/**
 * Checks if a role can perform a specific transition
 * @param {string} role - User role (clinic_admin, sales_rep, doctor, treatx_admin)
 * @param {string} fromStatus - Current status
 * @param {string} toStatus - Target status
 * @returns {object} { allowed: boolean, reason?: string }
 */
export const canRoleTransition = (role, fromStatus, toStatus) => {
  // First check if transition is valid at all
  const transitionValid = isValidTransition(fromStatus, toStatus);
  if (!transitionValid.valid) {
    return { allowed: false, reason: transitionValid.reason };
  }
  
  // Check role permission
  const transitionKey = `${fromStatus}->${toStatus}`;
  const allowedRoles = TRANSITION_PERMISSIONS[transitionKey];
  
  if (!allowedRoles) {
    // If not explicitly defined, only admin can do it
    if (role === 'provider_admin' || role === 'treatx_admin') {
      return { allowed: true };
    }
    return { allowed: false, reason: `No permission defined for ${transitionKey}` };
  }
  
  if (!allowedRoles.includes(role)) {
    return { 
      allowed: false, 
      reason: `Role '${role}' cannot transition from ${fromStatus} to ${toStatus}. Allowed roles: ${allowedRoles.join(', ')}` 
    };
  }
  
  return { allowed: true };
};

/**
 * Gets all transitions a role can perform from a given status
 * @param {string} role - User role
 * @param {string} currentStatus - Current lead status
 * @returns {string[]} Array of statuses the role can transition to
 */
export const getAvailableTransitionsForRole = (role, currentStatus) => {
  const allValidTransitions = getValidNextStatuses(currentStatus);
  
  return allValidTransitions.filter(toStatus => {
    const permission = canRoleTransition(role, currentStatus, toStatus);
    return permission.allowed;
  });
};

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Determines the "responsible" role for a lead based on its status
 * @param {string} status - Current lead status
 * @returns {string} Primary responsible role
 */
export const getResponsibleRole = (status) => {
  switch (status) {
    case 'NEW':
      return 'provider_admin'; // Admin assigns
    case 'ASSIGNED':
    case 'CONTACTED':
    case 'OFFER_SENT':
      return 'provider_sales_rep'; // Sales owns pre-commitment
    case 'DEPOSIT_PAID':
      return 'provider_admin'; // Handoff coordination
    case 'IN_TREATMENT':
    case 'POST_OP':
      return 'provider_doctor'; // Clinical responsibility
    case 'CLOSED_WON':
    case 'CLOSED_LOST':
    case 'CLOSED_CANCELLED':
      return null; // No active responsibility
    default:
      return 'provider_admin';
  }
};

/**
 * Gets the phase name for a status
 * @param {string} status - Lead status
 * @returns {string} Phase name
 */
export const getPhaseForStatus = (status) => {
  const preSales = ['NEW', 'ASSIGNED', 'CONTACTED'];
  const negotiation = ['OFFER_SENT'];
  const committed = ['DEPOSIT_PAID'];
  const clinical = ['IN_TREATMENT', 'POST_OP'];
  const terminal = ['CLOSED_WON', 'CLOSED_LOST', 'CLOSED_CANCELLED'];
  
  if (preSales.includes(status)) return 'PRE_SALES';
  if (negotiation.includes(status)) return 'NEGOTIATION';
  if (committed.includes(status)) return 'COMMITTED';
  if (clinical.includes(status)) return 'CLINICAL';
  if (terminal.includes(status)) return 'TERMINAL';
  return 'UNKNOWN';
};

/**
 * Checks if a status transition represents a "handoff" between roles
 * @param {string} fromStatus - Current status
 * @param {string} toStatus - Target status
 * @returns {boolean} True if this is a handoff transition
 */
export const isHandoffTransition = (fromStatus, toStatus) => {
  // Key handoff: Sales -> Clinical when deposit is paid and patient arrives
  if (fromStatus === 'DEPOSIT_PAID' && toStatus === 'IN_TREATMENT') {
    return true;
  }
  return false;
};

/**
 * Gets a human-readable description of a transition
 * @param {string} fromStatus - Current status
 * @param {string} toStatus - Target status
 * @returns {string} Description of what this transition means
 */
export const getTransitionDescription = (fromStatus, toStatus) => {
  const descriptions = {
    'NEW->ASSIGNED': 'Assign lead to a sales representative',
    'ASSIGNED->NEW': 'Unassign lead (return to pool)',
    'ASSIGNED->CONTACTED': 'Mark first meaningful patient contact',
    'CONTACTED->OFFER_SENT': 'Send treatment offer to patient',
    'OFFER_SENT->CONTACTED': 'Return to negotiation (patient has questions)',
    'OFFER_SENT->DEPOSIT_PAID': 'Confirm deposit payment received',
    'DEPOSIT_PAID->IN_TREATMENT': 'Patient arrived, treatment starting',
    'IN_TREATMENT->POST_OP': 'Treatment complete, entering recovery',
    'POST_OP->CLOSED_WON': 'Patient successfully discharged',
    'NEW->CLOSED_LOST': 'Lead lost (no contact made)',
    'ASSIGNED->CLOSED_LOST': 'Lead lost (pre-contact)',
    'CONTACTED->CLOSED_LOST': 'Lead lost (after contact)',
    'OFFER_SENT->CLOSED_LOST': 'Lead lost (offer rejected/expired)',
    'NEW->CLOSED_CANCELLED': 'Lead cancelled',
    'ASSIGNED->CLOSED_CANCELLED': 'Lead cancelled',
    'CONTACTED->CLOSED_CANCELLED': 'Lead cancelled',
    'OFFER_SENT->CLOSED_CANCELLED': 'Lead cancelled',
    'DEPOSIT_PAID->CLOSED_CANCELLED': 'Booking cancelled (refund)',
    'IN_TREATMENT->CLOSED_CANCELLED': 'Treatment cancelled mid-procedure',
    'POST_OP->CLOSED_CANCELLED': 'Case cancelled during recovery'
  };
  
  return descriptions[`${fromStatus}->${toStatus}`] || `Change status from ${fromStatus} to ${toStatus}`;
};

export default {
  VALID_TRANSITIONS,
  TRANSITION_PERMISSIONS,
  isValidTransition,
  getValidNextStatuses,
  canRoleTransition,
  getAvailableTransitionsForRole,
  getResponsibleRole,
  getPhaseForStatus,
  isHandoffTransition,
  getTransitionDescription
};
